import { createLogger } from "@civala/shared/logging"
import { createApiClient } from "@civala/shared/utils"

const log = createLogger({ app: "nexus" })

/**
 * Absolute origin of the @civala/api backend, for ALL `/api` calls (apiClient,
 * the raw multipart drop in useDropinApi, and the SSE EventSource in
 * usePipelineEvents).
 *
 * - **Dev:** empty → requests stay same-origin and the Vite proxy forwards
 *   `/api` to the backend (target configurable via NEXUS_API_PROXY).
 * - **Prod:** the deployed Cloudflare Pages site is static and has NO `/api`
 *   proxy, so same-origin `/api/*` would hit the SPA and 405. `.env.production`
 *   sets `VITE_API_BASE_URL=https://api.civalaos.com`, and the API CORS-allows the
 *   nexus origin, so the browser calls the backend directly cross-origin.
 */
export const apiBase = import.meta.env.VITE_API_BASE_URL ?? ""

/**
 * The nexus API client. Built on functype `Http` via `createApiClient`.
 * Uses {@link apiBase} so dev stays same-origin (Vite proxy) while prod targets
 * the deployed API directly. The pipeline hooks use the `*Either` methods and
 * branch on the result, so the client never throws here.
 */
export const apiClient = createApiClient({
  baseUrl: apiBase,
  onResponse: ({ method, url, status, requestId }) =>
    log.withMetadata({ method, url, status, requestId }).debug("api response"),
  onError: (error) => log.withMetadata({ ...error }).error("api request failed"),
})
